import { computed, ref } from 'vue';

/**
 * The zoom slider under a fixed crop frame (XN-4507, XN-4578), shared by the
 * logo crop and the photo crop on My details (XN-4561).
 *
 * 100% is the image just covering the frame; 300% the tightest crop allowed.
 * The zoom is DERIVED from the current crop against the widest one rather
 * than tracked: a tracked number was still 1 after a saved crop was restored,
 * so the slider read 100% while showing 250%, and its next move scaled from
 * the wrong baseline. The cropper must run with `:debounce="false"` and
 * `:transitions="false"`: a late or dropped change makes the relative zoom
 * below compound into a jump on release.
 */
export const ZOOM_MIN = 1;
export const ZOOM_MAX = 3;

const clamp = (value) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value));

export function useCropZoom(cropperEl) {
  const result = ref({ image: null, coordinates: null });
  const fitWidth = ref(0);

  const zoom = computed(() => (
    fitWidth.value && result.value.coordinates?.width
      ? clamp(fitWidth.value / result.value.coordinates.width)
      : 1
  ));

  // The cropper opens with the image just covering the frame, so its first
  // crop is the widest one: the 100% every later reading is measured against.
  // Call it once the image is ready, before anything moves the crop.
  function measure() {
    const first = cropperEl.value?.getResult();
    fitWidth.value = first?.coordinates?.width || 0;
    return first;
  }

  function onChange(change) {
    result.value = { image: change.image, coordinates: change.coordinates };
  }

  function setZoom(next) {
    const factor = clamp(Number(next)) / zoom.value;
    if (Math.abs(factor - 1) > 0.001) cropperEl.value?.zoom(factor);
  }

  return { result, zoom, measure, onChange, setZoom };
}
